import {
    type CSSProperties,
    useDeferredValue,
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    NCoreUIKitToast,
    NCoreUIKitTheme,
    formatPercent,
    formatNumber,
    ToggleGroup,
    ColorSlider,
    ColorField,
    SelectBox,
    Separator,
    Button,
    Dialog,
    Badge,
    Field,
    Card,
    Text
} from "ncore-ui-kit";
import {
    getKitLocale,
    useDocsI18n
} from "../i18n";
import {
    useDocsLayout
} from "../theme";
import {
    Shuffle,
    Check,
    Code,
    Info,
    Link,
    X
} from "lucide-react-native";
import {
    type CreateGreyTemperature,
    type CreatePreviewMessage,
    type CreateContrastCheck,
    CREATE_DEFAULT_SETTINGS,
    type CreatePreviewKind,
    type CreateCornerShape,
    type CreateReadingSize,
    CREATE_MESSAGE_SOURCE,
    type CreateChartCheck,
    type CreateSharpness,
    type CreateSettings,
    type CreateThemeKey,
    type CreateDensity,
    type CreatePreset,
    CREATE_VIEW_ID
} from "../create/settings";
import {
    getBrandFromControls,
    getBrandControls,
    generatePalette,
    getPresetBrand,
    normalizeBrand
} from "../create/generator";
import {
    getLuminance,
    parseColor,
    getDeltaE
} from "../create/color";
import {
    encodeSettings,
    decodeSettings
} from "../create/url";
import {
    parseDocsPathname,
    getDocsHref
} from "../router";
import {
    createKitIcon
} from "../components/kitIcons";
import {
    getProjectThemesCode
} from "../create/code";
import {
    BLOCK_VIEW_PATH
} from "../blocks/settings";
import {
    getContrastReport
} from "../create/report";
import {
    CodeBlock
} from "../components/content";
import {
    CREATE_PATH
} from "../nav";

type CreateOptionField<T extends string> = {
    onChange: (value: T) => void;
    options: Array<T>;
    prefix: string;
    title: string;
    value: T;
};

type CreateSelectOption = {
    title: string;
    id: string;
};

type CreateShuffleResult = {
    failures: number;
    brand: string;
};

type CreateColorControls = {
    chroma: number;
    hue: number;
};

const PRESETS: Array<CreatePreset> = [
    "nibgat",
    "nibgat-community",
    "caycore",
    "custom"
];

const GREY_TEMPERATURES: Array<CreateGreyTemperature> = [
    "auto",
    "pure",
    "warm",
    "cool"
];

const THEME_KEYS: Array<CreateThemeKey> = [
    "light",
    "dark"
];

const DENSITIES: Array<CreateDensity> = [
    "spacious",
    "compact"
];

const SHARPNESSES: Array<CreateSharpness> = [
    "sharp",
    "soft",
    "rounded"
];

const CORNER_SHAPES: Array<CreateCornerShape> = [
    "squircle",
    "round"
];

const PREVIEWS: Array<CreatePreviewKind> = [
    "components",
    "dashboard-01",
    "login-01",
    "settings-01",
    "chat-01",
    "reading"
];

const READING_SIZES: Array<CreateReadingSize> = [
    "readingSmallSize",
    "readingMediumSize",
    "readingLargeSize"
];

const SHUFFLE_CHROMA_MIN = 0.55;

const SHUFFLE_CHROMA_MAX = 0.95;

const SHUFFLE_ATTEMPTS = 24;

const BRAND_ADJUST_MIN_DELTA = 1;

const URL_SYNC_DELAY = 300;

const HUE_MAX = 359;

const PERCENT = 100;

const ShuffleKitIcon = createKitIcon(Shuffle);

const CheckKitIcon = createKitIcon(Check);

const CodeKitIcon = createKitIcon(Code);

const LinkKitIcon = createKitIcon(Link);

const CrossKitIcon = createKitIcon(X);

const InfoKitIcon = createKitIcon(Info);

const stylesheet = StyleSheet.create({
    reportHeader: {
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    stage: {
        alignSelf: "flex-start",
        flexGrow: 1
    },
    column: {
        flexDirection: "column"
    },
    workspace: {
        alignItems: "stretch",
        width: "100%"
    },
    swatchPair: {
        flexDirection: "row",
        overflow: "hidden",
        flexShrink: 0
    },
    actions: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    reportRow: {
        flexDirection: "row",
        alignItems: "center"
    },
    reportText: {
        flexShrink: 1,
        flexGrow: 1
    },
    badge: {
        flexShrink: 0
    },
    action: {
        flexGrow: 1
    }
});

const getColorControls = (brand: string): CreateColorControls => {
    const controls = getBrandControls(brand);

    return {
        chroma: Math.round(controls.saturation * PERCENT),
        hue: Math.round(controls.hue) % (HUE_MAX + 1)
    };
};

const getBrandDelta = (from: string, to: string) => {
    const first = parseColor(from);

    const second = parseColor(to);

    return first && second ? getDeltaE(first.rgb, second.rgb) : 0;
};

const getBrandLuminance = (brand: string) => {
    const parsed = parseColor(brand);

    return parsed ? getLuminance(parsed.rgb) : 0;
};

const getQuery = (settings: CreateSettings) => encodeSettings(settings).replace(/^\?/, "");

const getCreateHref = (settings: CreateSettings) => {
    const query = getQuery(settings);

    return query ? `${CREATE_PATH}?${query}` : CREATE_PATH;
};

const readInitialSettings = (): CreateSettings => {
    try {
        return decodeSettings(window.location.search);
    } catch {
        return CREATE_DEFAULT_SETTINGS;
    }
};

const getFailingChecks = (report: Array<CreateContrastCheck>) => report.filter((check) => check.isGated && !check.isPassing);

const getFailureCount = (report: Array<CreateContrastCheck>) => getFailingChecks(report).length;

const getReferenceFailures = () => new Set(getFailingChecks(getContrastReport(generatePalette(CREATE_DEFAULT_SETTINGS))).map((check) => check.id));

const floorTo = (value: number, digits: number) => {
    const factor = 10 ** digits;

    return Math.floor(Math.abs(value) * factor) / factor;
};

const SwatchPair = ({
    fg,
    bg
}: {
    fg: string;
    bg: string;
}) => {
    const {
        avatarSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext();

    const swatchStyle = {
        height: avatarSizes.xxSmall,
        width: avatarSizes.xxSmall
    };

    return <View
        style={[
            stylesheet.swatchPair,
            {
                borderColor: colors.content.border.default,
                borderWidth: borders.line,
                borderRadius: radiuses.xs
            }
        ]}
        aria-hidden={true}
    >
        <View
            style={[
                swatchStyle,
                {
                    backgroundColor: fg
                }
            ]}
        />
        <View
            style={[
                swatchStyle,
                {
                    backgroundColor: bg
                }
            ]}
        />
    </View>;
};

const ContrastRow = ({
    check
}: {
    check: CreateContrastCheck;
}) => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        spaces
    } = NCoreUIKitTheme.useContext();

    const locale = getKitLocale(language);

    const formatValue = (value: number, digits: number) => formatNumber({
        options: {
            maximumFractionDigits: digits
        },
        value: floorTo(value, digits),
        locale
    });

    const formatSeries = (series: Array<number>) => series.map((index) => formatValue(index, 0));

    const getChartDetails = (chart: CreateChartCheck) => {
        const [
            value = 0,
            secondValue = 0
        ] = chart.values;

        const [
            target = 0,
            secondTarget = 0
        ] = chart.targets;

        const [
            first = "",
            second = ""
        ] = formatSeries(chart.series);

        switch(chart.key) {
            case "lightness":
                return [
                    t("create.chart.lightness", [
                        formatValue(value, 3),
                        formatValue(secondValue, 3),
                        formatValue(target, 2),
                        formatValue(secondTarget, 2)
                    ])
                ];
            case "chroma":
                return [
                    t("create.chart.chroma", [
                        first,
                        formatValue(value, 3),
                        formatValue(target, 2)
                    ])
                ];
            case "contrast":
                return [
                    t("create.chart.contrast", [
                        first,
                        formatValue(value, 2),
                        formatValue(target, 0)
                    ])
                ];
            case "cvd":
                return [
                    t("create.chart.cvd", [
                        first,
                        second,
                        formatValue(value, 1),
                        t(`create.cvd.${chart.worstKind ?? "protan"}`),
                        formatValue(target, 0)
                    ]),
                    t("create.chart.tritan", [
                        formatValue(secondValue, 1)
                    ])
                ];
            case "normal":
                return [
                    t("create.chart.pairTarget", [
                        first,
                        second,
                        formatValue(value, 1),
                        formatValue(target, 0)
                    ])
                ];
            case "spread":
                return [
                    t("create.chart.pair", [
                        first,
                        second,
                        formatValue(value, 1)
                    ])
                ];
        }
    };

    const getDetails = () => {
        if(check.chart) {
            return getChartDetails(check.chart);
        }

        const details: Array<string> = [];

        if(check.wcagTarget > 0) {
            details.push(t("create.wcagTarget", [
                formatValue(check.wcag, 2),
                formatValue(check.wcagTarget, 1)
            ]));
        } else {
            details.push(t("create.wcagValue", [
                formatValue(check.wcag, 2)
            ]));
        }

        if(check.isApcaInfo) {
            details.push(t("create.apcaInfo", [
                formatValue(check.apca, 1)
            ]));
        } else if(check.apcaTarget > 0) {
            details.push(t("create.apcaTarget", [
                formatValue(check.apca, 1),
                formatValue(check.apcaTarget, 0)
            ]));
        } else if(check.apca !== 0) {
            details.push(t("create.apcaValue", [
                formatValue(check.apca, 1)
            ]));
        }

        return details;
    };

    return <View
        style={[
            stylesheet.reportRow,
            {
                paddingVertical: spaces.spacingXs,
                gap: spaces.spacingSm
            }
        ]}
        role="listitem"
    >
        <SwatchPair
            fg={check.fg}
            bg={check.bg}
        />
        <View
            style={stylesheet.reportText}
        >
            <Text
                variant="bodyMediumSize"
                color="high"
            >
                {t(check.labelKey)}
            </Text>
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {getDetails().join(" · ")}
            </Text>
            {
                check.chart ?
                    <Text
                        variant="bodySmallSize"
                        color="low"
                    >
                        {t(`create.chartHint.${check.chart.key}`)}
                    </Text>
                :
                    null
            }
        </View>
        <View
            style={stylesheet.badge}
        >
            {
                check.isGated ?
                    <Badge
                        title={check.isPassing ? t("create.pass") : t("create.fail")}
                        icon={check.isPassing ? CheckKitIcon : CrossKitIcon}
                        type={check.isPassing ? "success" : "danger"}
                        variant="tonal"
                        size="small"
                    />
                :
                    <Badge
                        title={t("create.info")}
                        icon={InfoKitIcon}
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
            }
        </View>
    </View>;
};

const CreatePage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const {
        shapeConfigs,
        avatarSizes,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        headerHeight
    } = useDocsLayout();

    const {
        height: windowHeight,
        width
    } = useWindowDimensions();

    const [
        settings,
        setSettings
    ] = useState<CreateSettings>(readInitialSettings);

    const [
        colorControls,
        setColorControls
    ] = useState<CreateColorControls>(() => getColorControls(settings.brand));

    const [
        reportTheme,
        setReportTheme
    ] = useState<CreateThemeKey>(settings.themeKey);

    const [
        presetRevision,
        setPresetRevision
    ] = useState(0);

    const [
        isCodeOpen,
        setIsCodeOpen
    ] = useState(false);

    const frameRef = useRef<HTMLIFrameElement>(null);

    const controlsBrandRef = useRef(settings.brand);

    const paletteSettings = useDeferredValue(settings);

    const palette = useMemo(() => generatePalette(paletteSettings), [
        paletteSettings.greyTemperature,
        paletteSettings.preset,
        paletteSettings.brand
    ]);

    const report = useMemo(() => getContrastReport(palette), [
        palette
    ]);

    const stateRef = useRef({
        settings,
        palette
    });

    stateRef.current = {
        settings,
        palette
    };

    const frameSrc = useMemo(() => {
        const query = getQuery(stateRef.current.settings);

        const params = new URLSearchParams(query);

        params.set("lang", language);

        return `${BLOCK_VIEW_PATH}${CREATE_VIEW_ID}?${params.toString()}`;
    }, [
        language
    ]);

    const postState = useCallback(() => {
        const message: CreatePreviewMessage = {
            settings: stateRef.current.settings,
            palette: stateRef.current.palette,
            source: CREATE_MESSAGE_SOURCE
        };

        frameRef.current?.contentWindow?.postMessage(message, window.location.origin);
    }, []);

    useEffect(() => {
        const frame = requestAnimationFrame(postState);

        return () => {
            cancelAnimationFrame(frame);
        };
    }, [
        postState,
        settings,
        palette
    ]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if(event.origin !== window.location.origin || event.source !== frameRef.current?.contentWindow) {
                return;
            }

            if(event.data?.source !== CREATE_MESSAGE_SOURCE || event.data?.palette) {
                return;
            }

            postState();
        };

        window.addEventListener("message", handleMessage);

        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [
        postState
    ]);

    useEffect(() => {
        const timer = setTimeout(() => {
            const current = parseDocsPathname(window.location.pathname);

            if(current.path !== CREATE_PATH) {
                return;
            }

            try {
                window.history.replaceState(window.history.state, "", getDocsHref(getCreateHref(settings), current.language ?? "tr"));
            } catch {
                return;
            }
        }, URL_SYNC_DELAY);

        return () => {
            clearTimeout(timer);
        };
    }, [
        settings
    ]);

    useEffect(() => {
        if(settings.brand === controlsBrandRef.current) {
            return;
        }

        controlsBrandRef.current = settings.brand;

        setColorControls(getColorControls(settings.brand));
    }, [
        settings.brand
    ]);

    useEffect(() => {
        setReportTheme(settings.themeKey);
    }, [
        settings.themeKey
    ]);

    const code = useMemo(() => isCodeOpen ? getProjectThemesCode(settings, palette) : "", [
        isCodeOpen,
        settings,
        palette
    ]);

    const isSplit = width >= shapeConfigs.wideBreakpoint;

    const stageHeight = Math.max(windowHeight - headerHeight - spaces.spacingXxl * 2, shapeConfigs.cardWidth);

    const codeMaxHeight = Math.max(windowHeight - shapeConfigs.cardWidth, shapeConfigs.chartHeight.small);

    const themeReport = report.filter((check) => check.themeKey === reportTheme);

    const gatedCount = themeReport.filter((check) => check.isGated).length;

    const failureCount = getFailureCount(themeReport);

    const adjustedBrand = palette.brandAdjusted.light;

    const isBrandAdjusted = paletteSettings.preset === "custom" && getBrandDelta(paletteSettings.brand, adjustedBrand) >= BRAND_ADJUST_MIN_DELTA;

    const updateSettings = (patch: Partial<CreateSettings>) => {
        setSettings((current) => ({
            ...current,
            ...patch
        }));
    };

    const applyCustomBrand = (brand: string, patch?: Partial<CreateSettings>) => {
        if(settings.preset !== "custom") {
            setPresetRevision((current) => current + 1);
        }

        updateSettings({
            ...patch,
            preset: "custom",
            brand
        });
    };

    const handlePresetChange = (preset: CreatePreset) => {
        if(preset === settings.preset) {
            return;
        }

        if(preset === "custom") {
            updateSettings({
                preset
            });

            return;
        }

        updateSettings({
            brand: getPresetBrand(preset),
            greyTemperature: "auto",
            preset
        });
    };

    const handleBrandChange = (value: string) => {
        const brand = normalizeBrand(value);

        if(brand && brand !== settings.brand) {
            applyCustomBrand(brand);
        }
    };

    const handleColorControlsChange = (patch: Partial<CreateColorControls>) => {
        const nextControls = {
            ...colorControls,
            ...patch
        };

        const brand = getBrandFromControls({
            lightness: getBrandControls(settings.brand).lightness,
            saturation: nextControls.chroma / PERCENT,
            hue: nextControls.hue
        });

        controlsBrandRef.current = brand;

        setColorControls(nextControls);

        if(brand !== settings.brand) {
            applyCustomBrand(brand);
        }
    };

    const handleGreyChange = (greyTemperature: CreateGreyTemperature) => {
        if(settings.preset === "custom") {
            updateSettings({
                greyTemperature
            });

            return;
        }

        applyCustomBrand(settings.brand, {
            greyTemperature
        });
    };

    const handleShuffle = () => {
        const lightness = getBrandControls(CREATE_DEFAULT_SETTINGS.brand).lightness;

        const referenceFailures = getReferenceFailures();

        let best: CreateShuffleResult | null = null;

        for(let attempt = 0; attempt < SHUFFLE_ATTEMPTS; attempt++) {
            const candidate = getBrandFromControls({
                saturation: SHUFFLE_CHROMA_MIN + Math.random() * (SHUFFLE_CHROMA_MAX - SHUFFLE_CHROMA_MIN),
                hue: Math.random() * (HUE_MAX + 1),
                lightness
            });

            const candidatePalette = generatePalette({
                ...settings,
                preset: "custom",
                brand: candidate
            });

            const failures = getFailingChecks(getContrastReport(candidatePalette)).filter((check) => !referenceFailures.has(check.id)).length;

            if(!best || failures < best.failures) {
                best = {
                    brand: candidatePalette.brandAdjusted.light,
                    failures
                };
            }

            if(failures === 0) {
                break;
            }
        }

        if(best) {
            applyCustomBrand(best.brand);
        }
    };

    const handleCopyLink = async () => {
        const url = `${window.location.origin}${getCreateHref(settings)}`;

        let isCopied = false;

        try {
            await navigator.clipboard.writeText(url);

            isCopied = true;
        } catch {
            isCopied = false;
        }

        NCoreUIKitToast.open({
            title: isCopied ? t("create.linkCopied") : t("create.linkCopyFailed"),
            type: isCopied ? "success" : "warning"
        });
    };

    const handleCodeClose = ({
        closeAnimation
    }: {
        closeAnimation: () => void;
    }) => {
        closeAnimation();
    };

    const getOptionTitle = (option: CreateSelectOption) => option.title;

    const getOptionKey = (option: CreateSelectOption) => option.id;

    const getSelectOptions = (prefix: string, values: Array<string>): Array<CreateSelectOption> => values.map((value) => ({
        title: t(`${prefix}.${value}`),
        id: value
    }));

    const renderOptionField = <T extends string>({
        onChange,
        options,
        prefix,
        title,
        value
    }: CreateOptionField<T>) => {
        const handleChange = (next: string | Array<string>) => {
            const option = options.find((item) => item === next);

            if(option) {
                onChange(option);
            }
        };

        return <Field>
            <Field.Title>
                {title}
            </Field.Title>
            <ToggleGroup
                onValueChange={handleChange}
                attachBehaviour="attached"
                selectBehaviour="single"
                variant="outline"
                title={title}
                value={value}
                size="small"
            >
                {
                    options.map((option) => <ToggleGroup.Item
                        title={t(`${prefix}.${option}`)}
                        value={option}
                        key={option}
                    />)
                }
            </ToggleGroup>
        </Field>;
    };

    const renderIntro = () => <View
        style={[
            stylesheet.column,
            {
                gap: spaces.spacingSm
            }
        ]}
    >
        <Text
            variant="headlineLargeSize"
            aria-level={1}
            role="heading"
            color="high"
        >
            {t("create.title")}
        </Text>
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {t("create.lead")}
        </Text>
    </View>;

    const renderActions = () => <View
        aria-label={t("create.actions")}
        style={[
            stylesheet.actions,
            {
                gap: spaces.spacingSm
            }
        ]}
        role="group"
    >
        <Button
            onPress={() => setIsCodeOpen(true)}
            title={t("create.getCode")}
            style={stylesheet.action}
            aria-haspopup="dialog"
            icon={CodeKitIcon}
            variant="filled"
            type="primary"
            size="small"
        />
        <Button
            title={t("create.shuffle")}
            style={stylesheet.action}
            onPress={handleShuffle}
            icon={ShuffleKitIcon}
            variant="outline"
            type="neutral"
            size="small"
        />
        <Button
            title={t("create.copyLink")}
            style={stylesheet.action}
            onPress={handleCopyLink}
            icon={LinkKitIcon}
            variant="outline"
            type="neutral"
            size="small"
        />
    </View>;

    const renderColorControls = () => <Field.Set>
        <Field.Legend>
            {t("create.colorLegend")}
        </Field.Legend>
        <Field.Group>
            <Field>
                <Field.Title>
                    {t("create.preset")}
                </Field.Title>
                <SelectBox
                    onChange={(selectedItems) => {
                        const preset = PRESETS.find((item) => item === selectedItems[0]?.__key);

                        if(preset) {
                            handlePresetChange(preset);
                        }
                    }}
                    data={getSelectOptions("create.preset", PRESETS)}
                    key={`preset-${language}-${presetRevision}`}
                    hintText={t("create.presetHint")}
                    titleExtractor={getOptionTitle}
                    defaultValue={settings.preset}
                    keyExtractor={getOptionKey}
                    spreadBehaviour="stretch"
                    size="small"
                />
            </Field>
            <ColorField
                onChange={handleBrandChange}
                spreadBehaviour="stretch"
                title={t("create.brand")}
                value={settings.brand}
                size="small"
            />
            <ColorSlider
                onChange={(value) => handleColorControlsChange({
                    hue: value
                })}
                formatValueText={(value) => `${value}°`}
                value={colorControls.hue}
                title={t("create.hue")}
                isChromaRelative={true}
                color={settings.brand}
                channel="hue"
                size="small"
            />
            <ColorSlider
                onChange={(value) => handleColorControlsChange({
                    chroma: Math.round(value * PERCENT)
                })}
                formatValueText={(value) => formatPercent({
                    locale: getKitLocale(language),
                    value
                })}
                value={colorControls.chroma / PERCENT}
                hintText={t("create.chromaHint")}
                title={t("create.chroma")}
                isChromaRelative={true}
                color={settings.brand}
                channel="chroma"
                size="small"
            />
            {renderOptionField({
                value: settings.greyTemperature,
                onChange: handleGreyChange,
                options: GREY_TEMPERATURES,
                title: t("create.grey"),
                prefix: "create.grey"
            })}
        </Field.Group>
    </Field.Set>;

    const renderStyleControls = () => <Field.Set>
        <Field.Legend>
            {t("create.styleLegend")}
        </Field.Legend>
        <Field.Group>
            {renderOptionField({
                onChange: (themeKey) => updateSettings({
                    themeKey
                }),
                title: t("create.theme"),
                value: settings.themeKey,
                prefix: "create.theme",
                options: THEME_KEYS
            })}
            {renderOptionField({
                onChange: (density) => updateSettings({
                    density
                }),
                title: t("create.density"),
                prefix: "create.density",
                value: settings.density,
                options: DENSITIES
            })}
            {renderOptionField({
                onChange: (sharpness) => updateSettings({
                    sharpness
                }),
                title: t("create.sharpness"),
                prefix: "create.sharpness",
                value: settings.sharpness,
                options: SHARPNESSES
            })}
            {renderOptionField({
                onChange: (cornerShape) => updateSettings({
                    cornerShape
                }),
                value: settings.cornerShape,
                title: t("create.corner"),
                prefix: "create.corner",
                options: CORNER_SHAPES
            })}
        </Field.Group>
    </Field.Set>;

    const renderPreviewControls = () => <Field.Set>
        <Field.Legend>
            {t("create.previewLegend")}
        </Field.Legend>
        <Field.Group>
            <Field>
                <Field.Title>
                    {t("create.preview")}
                </Field.Title>
                <SelectBox
                    onChange={(selectedItems) => {
                        const preview = PREVIEWS.find((item) => item === selectedItems[0]?.__key);

                        if(preview) {
                            updateSettings({
                                preview
                            });
                        }
                    }}
                    data={getSelectOptions("create.preview", PREVIEWS)}
                    defaultValue={settings.preview}
                    titleExtractor={getOptionTitle}
                    keyExtractor={getOptionKey}
                    key={`preview-${language}`}
                    spreadBehaviour="stretch"
                    size="small"
                />
            </Field>
            {
                settings.preview === "reading" ?
                    renderOptionField({
                        onChange: (readingSize) => updateSettings({
                            readingSize
                        }),
                        title: t("create.readingSize"),
                        prefix: "create.readingSize",
                        value: settings.readingSize,
                        options: READING_SIZES
                    })
                :
                    null
            }
        </Field.Group>
    </Field.Set>;

    const renderBrandNote = () => {
        if(!isBrandAdjusted) {
            return null;
        }

        const isDarkened = getBrandLuminance(adjustedBrand) < getBrandLuminance(paletteSettings.brand);

        return <View
            style={[
                stylesheet.column,
                {
                    gap: spaces.spacingXs
                }
            ]}
        >
            <Text
                variant="bodySmallSize"
                color="mid"
            >
                {t(isDarkened ? "create.brandDarkened" : "create.brandLightened", [
                    paletteSettings.brand,
                    adjustedBrand
                ])}
            </Text>
            <View
                style={stylesheet.actions}
            >
                <Button
                    onPress={() => applyCustomBrand(adjustedBrand)}
                    title={t("create.useAdjusted")}
                    variant="outline"
                    type="neutral"
                    size="xSmall"
                />
            </View>
        </View>;
    };

    const renderReport = () => <View
        style={[
            stylesheet.column,
            {
                gap: spaces.spacingMd
            }
        ]}
    >
        <View
            style={[
                stylesheet.reportHeader,
                {
                    gap: spaces.spacingSm
                }
            ]}
        >
            <Text
                variant="titleMediumSize"
                aria-level={2}
                role="heading"
                color="high"
            >
                {t("create.reportTitle")}
            </Text>
            <ToggleGroup
                onValueChange={(next) => {
                    const themeKey = THEME_KEYS.find((item) => item === next);

                    if(themeKey) {
                        setReportTheme(themeKey);
                    }
                }}
                title={t("create.reportTheme")}
                attachBehaviour="attached"
                selectBehaviour="single"
                value={reportTheme}
                variant="outline"
                size="small"
            >
                {
                    THEME_KEYS.map((themeKey) => <ToggleGroup.Item
                        title={t(`create.theme.${themeKey}`)}
                        value={themeKey}
                        key={themeKey}
                    />)
                }
            </ToggleGroup>
        </View>
        <Text
            variant="bodySmallSize"
            color="mid"
        >
            {t("create.reportLead")}
        </Text>
        <Text
            variant="bodyMediumSize"
            aria-live="polite"
            color="high"
        >
            {
                failureCount === 0 ?
                    t("create.summaryPass", [
                        gatedCount
                    ])
                :
                    t("create.summaryFail", [
                        gatedCount,
                        failureCount
                    ])
            }
        </Text>
        {renderBrandNote()}
        <View
            style={stylesheet.column}
            role="list"
        >
            {
                themeReport.map((check) => <ContrastRow
                    key={`${check.themeKey}-${check.id}`}
                    check={check}
                />)
            }
        </View>
    </View>;

    const frameStyle: CSSProperties = {
        height: isSplit ? stageHeight : Math.max(stageHeight - spaces.spacingXxl, shapeConfigs.cardWidth),
        display: "block",
        width: "100%",
        border: 0
    };

    const renderStage = () => <View
        style={[
            stylesheet.stage,
            isSplit ? {
                top: headerHeight + spaces.spacingXxl,
                position: "sticky" as never,
                minWidth: 0
            } : {
                alignSelf: "stretch"
            }
        ]}
    >
        <Card
            spreadBehaviour="stretch"
            variant="inset"
        >
            <Card.Content
                isFlush={true}
            >
                <iframe
                    title={t("create.frameTitle")}
                    style={frameStyle}
                    onLoad={postState}
                    ref={frameRef}
                    src={frameSrc}
                />
            </Card.Content>
        </Card>
    </View>;

    return <View
        style={[
            stylesheet.workspace,
            {
                gap: isSplit ? spaces.spacingXl : spaces.spacingXxl,
                flexDirection: isSplit ? "row" : "column"
            }
        ]}
    >
        <View
            style={[
                stylesheet.column,
                {
                    width: isSplit ? shapeConfigs.cardWidth : "100%",
                    gap: spaces.spacingXl,
                    flexShrink: 0
                }
            ]}
        >
            {renderIntro()}
            {renderActions()}
            <Separator/>
            {renderColorControls()}
            <Separator/>
            {renderStyleControls()}
            <Separator/>
            {renderPreviewControls()}
            {
                isSplit ?
                    <Separator/>
                :
                    null
            }
            {isSplit ? renderReport() : null}
        </View>
        {renderStage()}
        {isSplit ? null : renderReport()}
        <Dialog
            content={t("create.codeDescription")}
            onClosed={() => setIsCodeOpen(false)}
            onOverlayPress={handleCodeClose}
            contentJustify="language-based"
            title={t("create.codeTitle")}
            isOpen={isCodeOpen}
            variant="info"
            size="large"
        >
            <View
                style={{
                    minHeight: avatarSizes.xLarge
                }}
            >
                <CodeBlock
                    maxHeight={codeMaxHeight}
                    language="ts"
                    code={code}
                />
            </View>
        </Dialog>
    </View>;
};
export default CreatePage;
